import { toast } from '@/hooks/use-toast';
import { useUpdateProcurementStatus } from '@/hooks/use-procurement';
import { getProcurementById } from '@/services/procurementService';
import { 
  sendTelegramNotification, 
  formatApprovedProcurementNotification, 
  formatRejectedProcurementNotification,
  formatStatusChangeNotification 
} from '@/services/telegramService';

export const handleApproveAction = async (
  id: string, 
  updateStatus: ReturnType<typeof useUpdateProcurementStatus>, 
  showToast: boolean = true,
  approvedBy?: string
) => {
  try {
    // After finance approval, send to quality inspection (not warehouse)
    // Upfront flow: pending_finance → pending_quality → pending_warehouse → completed
    console.log(`Updating procurement ${id} to status: pending_quality`);
    
    // Prepare update data with audit trail
    const updateData: any = { 
      id, 
      status: 'pending_quality',
      finance_approved_at: new Date().toISOString()
    };
    
    if (approvedBy) {
      updateData.approved_by = approvedBy;
    }
    
    const result = await updateStatus.mutateAsync(
      updateData,
      {
        onSuccess: async (updatedProcurement) => {
          if (showToast) {
            toast({
              title: "Procurement Approved",
              description: "The procurement has been approved and moved to the next stage.",
            });
          }
          
          // Fetch full procurement details with user info for notification
          try {
            const fullProcurement = await getProcurementById(id);
            if (fullProcurement) {
              // Get approver info - either from the passed parameter or from the updated procurement
              const approverInfo = approvedBy ? { id: approvedBy } : null;
              
              const notificationMessage = formatApprovedProcurementNotification(fullProcurement, approverInfo);
              sendTelegramNotification(notificationMessage, id)
                .then((result) => {
                  if (result.success && result.sent > 0) {
                    console.log(`Telegram notification sent to ${result.sent} recipients for procurement approval`);
                  }
                })
                .catch(error => {
                  console.error('Error sending Telegram approval notification:', error);
                });
            }
          } catch (error) {
            console.error('Error fetching procurement for notification:', error);
          }
        },
        onError: (error) => {
          console.error("Update failed:", error);
          toast({
            title: "Update Failed",
            description: "Could not approve procurement. Error: " + (error.message || "Unknown error"),
            variant: "destructive",
          });
        }
      }
    );
    return true;
  } catch (error) {
    console.error("Error updating status:", error);
    toast({
      title: "Error",
      description: "An unexpected error occurred: " + (error.message || "Unknown error"),
      variant: "destructive",
    });
    return false;
  }
};

export const handleRejectAction = async (
  id: string, 
  updateStatus: ReturnType<typeof useUpdateProcurementStatus>,
  rejectionReason?: string,
  rejectedBy?: string
) => {
  try {
    console.log(`Updating procurement ${id} to status: rejected`);
    
    // Prepare update data with audit trail
    const updateData: any = { 
      id, 
      status: 'rejected',
      finance_rejected_at: new Date().toISOString()
    };
    
    if (rejectionReason) {
      updateData.rejection_reason = rejectionReason;
    }
    
    if (rejectedBy) {
      updateData.rejected_by = rejectedBy;
    }
    
    const result = await updateStatus.mutateAsync(
      updateData,
      {
        onSuccess: async (updatedProcurement) => {
          toast({
            title: "Procurement Rejected",
            description: "The procurement has been rejected.",
          });
          
          // Fetch full procurement details with user info for notification
          try {
            const fullProcurement = await getProcurementById(id);
            if (fullProcurement) {
              // Get rejector info - either from the passed parameter or from the updated procurement
              const rejectorInfo = rejectedBy ? { id: rejectedBy } : null;
              
              const notificationMessage = formatRejectedProcurementNotification(
                fullProcurement, 
                rejectorInfo, 
                rejectionReason
              );
              sendTelegramNotification(notificationMessage, id)
                .then((result) => {
                  if (result.success && result.sent > 0) {
                    console.log(`Telegram notification sent to ${result.sent} recipients for procurement rejection`);
                  }
                })
                .catch(error => {
                  console.error('Error sending Telegram rejection notification:', error);
                });
            }
          } catch (error) {
            console.error('Error fetching procurement for notification:', error);
          }
        },
        onError: (error) => {
          console.error("Update failed:", error);
          toast({
            title: "Update Failed",
            description: "Could not reject procurement. Error: " + (error.message || "Unknown error"),
            variant: "destructive",
          });
        }
      }
    );
    return true;
  } catch (error) {
    console.error("Error updating status:", error);
    toast({
      title: "Error",
      description: "An unexpected error occurred: " + (error.message || "Unknown error"),
      variant: "destructive",
    });
    return false;
  }
};

/**
 * Generic function to handle procurement status changes with notifications
 */
export const handleStatusChange = async (
  id: string,
  newStatus: string, 
  oldStatus: string,
  updateStatus: ReturnType<typeof useUpdateProcurementStatus>,
  showToast: boolean = true
) => {
  try {
    console.log(`Updating procurement ${id} from status ${oldStatus} to: ${newStatus}`);
    const result = await updateStatus.mutateAsync(
      { id, status: newStatus },
      {
        onSuccess: async (updatedProcurement) => {
          if (showToast) {
            toast({
              title: "Status Updated",
              description: `The procurement status has been updated to ${newStatus}.`,
            });
          }
          
          // Fetch full procurement details with user info for notification
          try {
            console.log('Fetching enriched procurement data for status change notification');
            const fullProcurement = await getProcurementById(id);
            if (fullProcurement) {
              console.log('Enriched procurement data retrieved:', {
                id: fullProcurement.id,
                product_name: fullProcurement.product_name,
                total_quantity: fullProcurement.total_quantity,
                total_amount: fullProcurement.total_amount,
                requestor: fullProcurement.requestor,
                source_name: fullProcurement.source_name
              });
              
              const notificationMessage = formatStatusChangeNotification(fullProcurement, oldStatus, newStatus);
              sendTelegramNotification(notificationMessage, id)
                .then((result) => {
                  if (result.success && result.sent > 0) {
                    console.log(`Telegram notification sent to ${result.sent} recipients for status change`);
                  }
                })
                .catch(error => {
                  console.error('Error sending Telegram status notification:', error);
                });
            } else {
              console.error('Failed to fetch enriched procurement data for notification');
            }
          } catch (error) {
            console.error('Error fetching procurement for status change notification:', error);
          }
        },
        onError: (error) => {
          console.error("Update failed:", error);
          toast({
            title: "Update Failed",
            description: `Could not update status. Error: ${error.message || "Unknown error"}`,
            variant: "destructive",
          });
        }
      }
    );
    return true;
  } catch (error) {
    console.error("Error updating status:", error);
    toast({
      title: "Error",
      description: `An unexpected error occurred: ${error.message || "Unknown error"}`,
      variant: "destructive",
    });
    return false;
  }
};
